import { useEffect, useState } from 'react'
import { useLocation, useParams, type To } from 'react-router-dom'
import { useNavigate } from '../../components/nav'
import { useSelector, useDispatch } from 'react-redux'
import { AlertCircle, LogIn, RefreshCw } from 'lucide-react'
import type { RootState, AppDispatch } from '../../store'
import BugReport, { BugReportActions } from '../../components/bug-management/BugReport'
import CommentsSection from '../../components/CommentsSection'
import BugMetadata from '../../components/bug-management/BugMetadata'
import BackNavButton from '../../components/BackNavButton'
import { login, postLoginReturnTo } from '../../components/UserMenu'
import { useSignInOnUnauthorized } from '../../hooks/useSignInOnUnauthorized'
import { isUnauthorizedError } from '../../lib/unauthorizedError'
import { useDeleteBugMutation, useGetBugQuery, useGetProjectQuery } from '../../services/projectDataApi'
import { getBugListLocation } from '../../lib/bug-management/listLocation'
import './BugDetail.css'

function BugDetailSkeleton() {
  return (
    <div className="bug-detail" aria-hidden="true" data-testid="bug-loading">
      <div className="bug-detail__layout">
        <div className="bug-detail__main">
          <div className="bug-detail__skeleton-card">
            <div className="bug-detail__skeleton-toolbar">
              <div className="skeleton skeleton--text" style={{ width: '140px', height: '16px' }} />
              <div className="skeleton skeleton--text" style={{ width: '110px', height: '32px', borderRadius: '6px' }} />
            </div>
            <div className="bug-detail__skeleton-title-row">
              <div className="skeleton skeleton--badge" />
              <div className="skeleton skeleton--text skeleton--title" />
            </div>
            <div className="bug-detail__skeleton-section">
              <div className="skeleton skeleton--text" style={{ width: '90px', height: '16px' }} />
              <div className="skeleton skeleton--text" style={{ width: '100%', height: '14px' }} />
              <div className="skeleton skeleton--text" style={{ width: '80%', height: '14px' }} />
            </div>
            <div className="bug-detail__skeleton-section">
              <div className="skeleton skeleton--text" style={{ width: '130px', height: '16px' }} />
              <div className="skeleton skeleton--text" style={{ width: '100%', height: '14px' }} />
              <div className="skeleton skeleton--text" style={{ width: '60%', height: '14px' }} />
            </div>
          </div>
        </div>
        <aside className="bug-detail__sidebar">
          <div className="bug-detail__skeleton-card">
            <div className="skeleton skeleton--text" style={{ width: '60px', height: '16px' }} />
            <div className="skeleton skeleton--badge" style={{ width: '80px' }} />
          </div>
          <div className="bug-detail__skeleton-card">
            <div className="skeleton skeleton--text" style={{ width: '60px', height: '16px' }} />
            <div className="skeleton skeleton--text" style={{ width: '100%', height: '14px' }} />
            <div className="skeleton skeleton--text" style={{ width: '100%', height: '14px' }} />
            <div className="skeleton skeleton--text" style={{ width: '100%', height: '14px' }} />
          </div>
        </aside>
      </div>
    </div>
  )
}

export default function BugDetail() {
  const { projectId, bugId } = useParams<{ projectId: string; bugId: string }>()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const location = useLocation()
  const user = useSelector((state: RootState) => state.auth.user)
  const bugQuery = useGetBugQuery(bugId!, { skip: !bugId })
  const projectQuery = useGetProjectQuery(projectId!, { skip: !projectId })
  const project = projectQuery.data
  const [deleteBug] = useDeleteBugMutation()
  const bug = bugQuery.data
  const loading = bugQuery.isLoading
  const error = bugQuery.error
    ? (isUnauthorizedError(bugQuery.error) ? 'Login required to view this bug' : 'Failed to fetch bug')
    : null
  // A bug in a private project opened while signed out goes to sign-in and comes back here, rather
  // than dead-ending on the error state below.
  const redirectingToLogin = useSignInOnUnauthorized(bugQuery.error)
  const bugListLocation = getBugListLocation(location.state, projectId)
  const openedFromSecurity = bugListLocation?.pathname.endsWith('/security') ?? false
  const backToProjectPath: To = bugListLocation ?? `/projects/${projectId}/overview`
  const backLabel = bugListLocation?.pathname.includes('/session-reviewers/') ? 'Back to Reviewer' : openedFromSecurity ? 'Back to Security' : bugListLocation ? 'Back to Bug Reports' : 'Back to Project'
  const [deleteError, setDeleteError] = useState<string | null>(null)

  useEffect(() => {
    if (bug) {
      document.title = `Replay QA: ${bug.project_name || 'Bug'}`
    }
    return () => { document.title = 'Replay QA' }
  }, [bug])

  const handleRetry = () => {
    void bugQuery.refetch()
  }

  const handleDelete = async () => {
    if (!bug) return
    if (!window.confirm('Are you sure you want to delete this bug? This action cannot be undone.')) return
    setDeleteError(null)
    try {
      await deleteBug({ id: bug.id, projectId: bug.project_id }).unwrap()
      navigate(bugListLocation ?? `/projects/${projectId}/bugs`)
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete bug')
    }
  }

  // The skeleton also covers the moment it takes the sign-in redirect to leave the page.
  if (loading || redirectingToLogin) {
    return <BugDetailSkeleton />
  }

  if (error || !bug) {
    return (
      <div className="bug-detail__error" role="alert" data-testid="bug-error">
        <AlertCircle size={20} aria-hidden="true" />
        <p className="bug-detail__error-message">{error ?? 'Bug not found'}</p>
        <div className="bug-detail__error-actions">
          {!user && error?.includes('Login required') && (
            <button type="button" className="bug-detail__retry-btn" onClick={() => login(dispatch, postLoginReturnTo(window.location.pathname, window.location.search))} data-testid="login-btn" data-analytics-event="bug-signin-button">
              <LogIn size={14} aria-hidden="true" />
              Sign in
            </button>
          )}
          <button type="button" className="bug-detail__retry-btn" onClick={handleRetry} data-testid="bug-retry" data-analytics-event="bug-retry-button">
            <RefreshCw size={14} aria-hidden="true" />
            Retry
          </button>
          <BackNavButton
            label={backLabel}
            onClick={() => navigate(backToProjectPath)}
            data-testid="back-to-project"
            data-analytics-event="bug-back"
          />
        </div>
      </div>
    )
  }

  return (
    <div className="bug-detail" data-testid="bug-detail-page">
      {deleteError && (
        <div className="bug-detail__mutation-error" role="alert" data-testid="delete-error">
          <AlertCircle size={14} aria-hidden="true" />
          {deleteError}
        </div>
      )}

      {/* GitHub / Graphite-style two-column page: the report and its discussion fill the wide
          column; status and metadata live in a narrow right rail. The status control lives in the
          rail only, so the report header shows status read-only. */}
      <div className="bug-detail__layout">
        <div className="bug-detail__main">
          <BugReport
            bug={bug}
            backLabel={backLabel}
            onNavigateBack={() => navigate(backToProjectPath)}
            onDeleteBug={handleDelete}
            showStatusSelector
          />
          <CommentsSection projectId={bug.project_id} targetType="bug" targetId={bug.id} />
        </div>
        <aside className="bug-detail__sidebar" aria-label="Bug details">
          <BugReportActions bug={bug} project={project} />
          <BugMetadata bug={bug} hasWebhook={Boolean(project?.webhook_url)} />
        </aside>
      </div>
    </div>
  )
}
